import OriginTracer from '@atlassiansox/origin-tracing';
import {
	COMPANY_MANAGED_PROJECT,
	SOFTWARE_PROJECT,
	CORE_PROJECT,
	TEAM_MANAGED_PROJECT,
	type JiraProjectStyle,
	type ProjectType,
} from '@atlassian/jira-common-constants/src/project-types.tsx';
import { getRelativeURLFromLocation } from '@atlassian/react-resource-router/src/controllers/router-store/utils/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { createRouterSelector } from '@atlassian/react-resource-router';
import {
	JIRA_SOFTWARE_CMP_PATH_PREFIX,
	JIRA_SOFTWARE_PATH_PREFIX,
	JIRA_BUSINESS_PATH_PREFIX,
} from './constants.tsx';
import type { OriginAnalyticsAttributes } from '@atlassiansox/origin-tracing/types';

const getProjectStyleFromPathname = (pathname: string): JiraProjectStyle | undefined => {
	// Only software projects have different routes for CMP and TMP. Most business routes
	// have 'classic' in the route name even though they're shared between CMP and TMP.
	if (pathname.startsWith(JIRA_SOFTWARE_CMP_PATH_PREFIX)) {
		return COMPANY_MANAGED_PROJECT;
	}
	if (pathname.startsWith(JIRA_SOFTWARE_PATH_PREFIX)) {
		return TEAM_MANAGED_PROJECT;
	}
	return undefined;
};

const getProjectTypeFromPathname = (pathname: string): ProjectType | undefined => {
	// Considered using:
	// 1. the route group, but there are some cases where that doesn't work e.g. the 'All work'
	// routes are all part of the 'issue-navigator' group, even though they do differ by project type
	// 2. the route name, but the naming convention is inconsistent between business routes e.g.
	// 'issue-navigator-core', 'classic-business-board'
	if (pathname.startsWith(JIRA_SOFTWARE_PATH_PREFIX)) {
		return SOFTWARE_PROJECT;
	}
	if (pathname.startsWith(JIRA_BUSINESS_PATH_PREFIX)) {
		return CORE_PROJECT;
	}
	return undefined;
};

type ProjectAttributes = {
	projectStyle?: JiraProjectStyle | undefined;
	projectType?: ProjectType | undefined;
};

export const getProjectAttributes = (pathname: string): ProjectAttributes => {
	return {
		projectStyle: getProjectStyleFromPathname(pathname),
		projectType: getProjectTypeFromPathname(pathname),
	};
};

export const useRelativeURLFromLocation = createRouterSelector(({ location }) => {
	if (fg('jira_client_side_error_handled_track_event')) {
		return getRelativeURLFromLocation(location);
	}
	return '';
});

/**
 * Returns the origin tracer attributes from the current router state
 */
export const getOriginAnalyticsAttributes = (
	url: string,
): OriginAnalyticsAttributes | undefined => {
	const origin = OriginTracer.fromUrl(url);
	return origin.isEmpty() ? undefined : origin.toAnalyticsAttributes();
};
